import { PortfolioComparison } from 'src/app/domain/portfolio-comparison/dto/PortfolioComparison';
import { Rows } from '../../interfaces';
import { PartialCell, formats, getCell, getStatusForSubTotal, withCellStyles } from '../../utils';
import { PeriodComparisonExcel, PeriodComparisonExportData } from './period-comparison';
import { CellStyle } from 'xlsx-js-style';
import { boldLightGrayBg } from '../financial-statement/capital-account/styles';

export const getPreparedData = (data: PeriodComparisonExportData, periods: PeriodComparisonExcel): Rows => {
  const firstPeriod = data.active[0].firstPeriod;
  const secondPeriod = data.active[0].secondPeriod;

  const stRow = ['', '', '', secondPeriod, '', '', null, '', '', 'Difference', '', '', null, '', '', firstPeriod, '', ''];
  const newData: Rows = [];
  newData.push(stRow);
  const row = [];
  periods.headerForDisplay.forEach((header) => (header === '' ? row.push(null) : row.push(withCellStyles(header, boldLightGrayBg))));
  newData.push(row);
  const activeData = data.active;
  const soldData = data.sold;
  const nameActiveRow = [];
  nameActiveRow.push(withCellStyles('Active', leftBold));
  newData.push(nameActiveRow);
  const activeRows = getManyRows(activeData, periods.headerForLogic);
  newData.push(...activeRows);
  const totalActive = getTotalMapping('active', activeData.length + 3, periods.headerForLogic);
  newData.push(totalActive);
  if (soldData?.length) {
    const nameSoldRow = [];
    nameSoldRow.push(withCellStyles('Realized', leftBold));
    newData.push(nameSoldRow);
    const soldRows = getManyRows(soldData, periods.headerForLogic, 'sold', activeData.length + 2);
    newData.push(...soldRows);
    const totalSold =
    getTotalMapping('sold', activeData.length + 3 + soldData.length + 2, periods.headerForLogic, 3 + activeData.length + 3);
    newData.push(totalSold);
    const grandTotal = getGrandTotalMapping(periods.headerForLogic, activeData.length + 1, soldData.length);
    newData.push(grandTotal);
  }
  return newData;
};

export const getManyRows = (data: PortfolioComparison[], headers: string[], status: string = 'active', activeLen = 0): Rows => {
  const rows: Rows = [];
  let addStartForSold = 0;
  if (status === 'sold') {
    addStartForSold += activeLen;
  }
  const mappedToHeaders = data.map((d, i) => getNewMapping(d, (i + addStartForSold)));
  for (const mapped of mappedToHeaders) {
    const row = [];
    for (const header of headers) {
      row.push(withCellStyles(mapped[header], left));
    }
    rows.push(row);
  }
  return rows;
};

export const getNewMapping = (comparison: PortfolioComparison, i: number): Category => {
  i += 4;
  const body: Category = {
    Company: comparison.companyName,
    'Investment(1)': getCell({
      v: comparison.costSecondPeriod,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value(1)': getCell({
      v: comparison.fmvSecondPeriod,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Gain/(Loss)(1)': getCell({
      f: `C${i}-B${i}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Multiple(1)': getCell({
      f: `IFERROR(C${i}/B${i},0)`,
      z: formats.multiplier,
      t: 'n',
    }),
    'Holding(1)': getCell({
      v: comparison.holdingSecondPeriod,
      z: formats.percents,
      t: 'n',
    }),
    empty1: '',
    Investment: getCell({
      f: `N${i}-B${i}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value': getCell({
      f: `O${i}-C${i}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Gain/(Loss)': getCell({
      f: `I${i}-H${i}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    Multiple: getCell({
      f: `Q${i}-E${i}`,
      z: formats.multiplier,
      t: 'n',
    }),
    Holding: getCell({
      f: `R${i}-F${i}`,
      z: formats.percents,
      t: 'n',
    }),
    empty2: '',
    'Investment(2)': getCell({
      v: comparison.costFirstPeriod,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value(2)': getCell({
      v: comparison.fmvFirstPeriod,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Gain/(Loss)(2)': getCell({
      f: `O${i}-N${i}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Multiple(2)': getCell({
      f: `IFERROR(O${i}/N${i},0)`,
      z: formats.multiplier,
      t: 'n',
    }), // add check that cost !== 0
    'Holding(2)': getCell({
      v: comparison.holdingFirstPeriod,
      z: formats.percents,
      t: 'n',
    }),
  };
  return body;
};

export const getTotalMapping = (status: string, len: number, headers: string[], startIndex = 4): PartialCell[] => {
  const rows: Rows = [];
  const row: PartialCell[] = [];
  const additionalLen = 1;

  const total: Category = {
    Company: `Total ${getStatusForSubTotal(status)}`,
    'Investment(1)': getCell({
      f: `SUM(B${startIndex}:B${len})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value(1)': getCell({
      f: `SUM(C${startIndex}:C${len})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Gain/(Loss)(1)': getCell({
      f: `SUM(D${startIndex}:D${len})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Multiple(1)': getCell({
      f: `IFERROR(C${len + additionalLen}/B${len + additionalLen},0)`,
      z: formats.multiplier,
      t: 'n',
    }),
    'Holding(1)': getCell({
      v: '',
      t: 's',
    }),
    empty1: '',
    Investment: getCell({
      f: `N${len + 1}-B${len + 1}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value': getCell({
      f: `O${len + 1}-C${len + 1}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Gain/(Loss)': getCell({
      f: `I${len + 1}-H${len + 1}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    Multiple: getCell({
      f: `Q${len + 1}-E${len + 1}`,
      z: formats.multiplier,
      t: 'n',
    }),
    Holding: getCell({
      v: '',
      t: 's',
    }),
    empty2: '',
    'Investment(2)': getCell({
      f: `SUM(N${startIndex}:N${len})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value(2)': getCell({
      f: `SUM(O${startIndex}:O${len})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Gain/(Loss)(2)': getCell({
      f: `SUM(P${startIndex}:P${len})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Multiple(2)': getCell({
      f: `IFERROR(O${len + additionalLen}/N${len + additionalLen},0)`,
      z: formats.multiplier,
      t: 'n',
    }),
    'Holding(2)': getCell({
      v: '',
      t: 's',
    }),
  };
  for (const header of headers) {
    row.push(withCellStyles(total[header], boldLightGrayBgLeft));
  }
  rows.push(row);
  return row;
};

export const getGrandTotalMapping = (headers: string[], activeLen: number, soldLen: number, startIndex = 3): PartialCell[] => {
  const rows: Rows = [];
  const row: PartialCell[] = [];
  startIndex = 3 + activeLen;
  const stopIndex = startIndex + soldLen + 2;
  const total: Category = {
    Company: `Grand total`,
    'Investment(1)': getCell({
      f: `SUM(B${startIndex},B${stopIndex})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value(1)': getCell({
      f: `SUM(C${startIndex},C${stopIndex})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Gain/(Loss)(1)': getCell({
      f: `SUM(D${startIndex},D${stopIndex})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Multiple(1)': getCell({
      f: `IFERROR(C${1 + stopIndex}/B${1 + stopIndex},0)`,
      z: formats.multiplier,
      t: 'n',
    }),
    'Holding(1)': getCell({
      v: '',
      t: 's',
    }),
    empty1: '',
    Investment: getCell({
      f: `N${stopIndex + 1}-B${stopIndex + 1}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value': getCell({
      f: `O${stopIndex + 1}-C${stopIndex + 1}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Gain/(Loss)': getCell({
      f: `I${stopIndex + 1}-H${stopIndex + 1}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    Multiple: getCell({
      f: `Q${stopIndex + 1}-E${stopIndex + 1}`,
      z: formats.multiplier,
      t: 'n',
    }),
    Holding: getCell({
      v: '',
      t: 's',
    }),
    empty2: '',
    'Investment(2)': getCell({
      f: `SUM(N${startIndex},N${stopIndex})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value(2)': getCell({
      f: `SUM(O${startIndex},O${stopIndex})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Gain/(Loss)(2)': getCell({
      f: `SUM(P${startIndex},P${stopIndex})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Multiple(2)': getCell({
      f: `IFERROR(O${1 + stopIndex}/N${1 + stopIndex},0)`,
      z: formats.multiplier,
      t: 'n',
    }),
    'Holding(2)': getCell({
      v: '',
      t: 's',
    }),
  };
  for (const header of headers) {
    row.push(withCellStyles(total[header], boldGrayBgLeft));
  }
  rows.push(row);
  return row;
};


const leftBold: CellStyle = {
  alignment: { horizontal: 'left' },
  font: { bold: true },
};

const left: CellStyle = {
  alignment: { horizontal: 'left' },
};

export const boldGrayBgLeft: CellStyle = {
  font: { bold: true },
  fill: { fgColor: { rgb: 'cacaca' } },
  alignment: { horizontal: 'left' },
};


export const boldLightGrayBgLeft: CellStyle = {
  font: { bold: true },
  fill: { fgColor: { rgb: 'e3e3e3' } },
  alignment: { horizontal: 'left' },
};

interface Category {
  'Company': string;
  'Investment(1)': PartialCell;
  'Fair value(1)': PartialCell;
  'Gain/(Loss)(1)': PartialCell;
  'Multiple(1)': PartialCell;
  'Holding(1)': PartialCell;
  empty1: string;
  'Investment': PartialCell;
  'Fair value': PartialCell;
  'Gain/(Loss)': PartialCell;
  'Multiple': PartialCell;
  'Holding': PartialCell;
  empty2: string;
  'Investment(2)': PartialCell;
  'Fair value(2)': PartialCell;
  'Gain/(Loss)(2)': PartialCell;
  'Multiple(2)': PartialCell;
  'Holding(2)': PartialCell;
}
